import React, { Fragment, useEffect, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import "../../assets/css/Permissions.css";
import { fetchAllUsers, fetchIssueById, updateIssue } from "./issueService";
import { fetchTaskById } from "../taskService";
import IssueFileAttachments from "./IssueFileAttachments";
import { getAuthItem } from "../../utils/authStorage";

function formatDateTime(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "—";
  }
}

function formatIssueDueDate(value) {
  if (!value) return "—";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  }
  return formatDateTime(s);
}

function InfoItem({ label, value }) {
  const hasValue = value != null && value !== "" && value !== "—";
  return (
    <div className="viewlead-info-item">
      <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
      <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? value : "—"}</span>
    </div>
  );
}

function parseIdList(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
}

export default function IssueDetailsPanel({ issue, issueId, canEdit = false, onUpdated }) {
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showReassignSection, setShowReassignSection] = useState(false);
  const [showAssigneesDropdown, setShowAssigneesDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reassignError, setReassignError] = useState("");
  const [lastReassignedTo, setLastReassignedTo] = useState([]);
  const [lastReassignedBy, setLastReassignedBy] = useState("");

  const [taskDetails, setTaskDetails] = useState(null);
  const [loadingTaskDetails, setLoadingTaskDetails] = useState(false);

  const assignedUsers = Array.isArray(issue?.Assigned_Users)
    ? issue.Assigned_Users
    : Array.isArray(issue?.assigned_users)
      ? issue.assigned_users
      : [];

  useEffect(() => {
    if (!issue) return;
    const assigned = Array.isArray(issue.Assigned_Users) ? issue.Assigned_Users : Array.isArray(issue.assigned_users) ? issue.assigned_users : [];
    const ids = assigned.map((u) => u.User_Id ?? u.user_id ?? u.id).filter((x) => x != null);
    setSelectedUserIds(ids);
  }, [issue?.Issue_Id, issue?.Assigned_Users]);

  useEffect(() => {
    const loadTaskDetails = async () => {
      const rawTaskId = issue?.Task_Id ?? issue?.task_id ?? issue?.Task_Id ?? issue?.TaskId;
      const taskId = rawTaskId != null ? Number(rawTaskId) : null;
      if (!taskId || !Number.isFinite(taskId)) {
        setTaskDetails(null);
        return;
      }
      setLoadingTaskDetails(true);
      try {
        const data = await fetchTaskById(taskId);
        setTaskDetails(data || null);
      } catch (err) {
        console.error("Error loading task details for issue view:", err);
        setTaskDetails(null);
      } finally {
        setLoadingTaskDetails(false);
      }
    };
    if (issue) loadTaskDetails();
  }, [issue?.Task_Id, issue?.task_id, issue]);

  useEffect(() => {
    const load = async () => {
      setLoadingUsers(true);
      try {
        const list = await fetchAllUsers();
        setUsers((list || []).sort((a, b) => String(a.name).localeCompare(String(b.name))));
      } catch (err) {
        console.error("Error loading users:", err);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (showAssigneesDropdown && !e.target.closest(".form-group-multi-select")) {
        setShowAssigneesDropdown(false);
      }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [showAssigneesDropdown]);

  const allUsersSelected = !loadingUsers && users.length > 0 && selectedUserIds.length === users.length;

  const handleToggleSelectAll = () => {
    setSelectedUserIds(allUsersSelected ? [] : users.map((u) => u.id));
  };

  const handleUserToggle = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const getCurrentUserName = () => {
    try {
      const u = getAuthItem("user");
      if (!u) return "—";
      const user = JSON.parse(u);
      return user.user_name ?? user.User_Name ?? user.name ?? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() ?? "—";
    } catch {
      return "—";
    }
  };

  const refreshIssue = async () => {
    if (!issueId) return;
    try {
      const data = await fetchIssueById(issueId);
      if (data && onUpdated) onUpdated(data);
    } catch (err) {
      console.error("Error refreshing issue:", err);
    }
  };

  const handleReassign = async () => {
    if (!canEdit || !issueId || !issue) return;
    setSaving(true);
    setReassignError("");
    try {
      const taskId = Number(issue.Task_Id ?? issue.task_id);
      const subTaskId = issue.Sub_Task_Id ?? issue.sub_task_id;
      const testCaseIds = parseIdList(issue.Test_Case_Ids ?? issue.test_case_ids ?? []);
      const payload = {
        Task_Id: taskId,
        Title: (issue.Title ?? issue.title ?? "").trim(),
        Description: (issue.Description ?? issue.description ?? "").trim() || null,
        Type: issue.Type ?? issue.type ?? "Bug",
        Issue_Layer: issue.Issue_Layer ?? issue.issue_layer ?? "Backend",
        Status_Id: Number(issue.Status_Id ?? issue.status_id ?? 2),
        Priority: issue.Priority ?? issue.priority ?? "Low",
        Assigned_User_Ids: (selectedUserIds || []).map((id) => Number(id)),
        Test_Case_Ids: testCaseIds
      };
      if (subTaskId != null) payload.Sub_Task_Id = Number(subTaskId);
      const dueRaw = issue.Due_Date ?? issue.due_date;
      if (dueRaw != null && String(dueRaw).trim() !== "") {
        payload.Due_Date = String(dueRaw).trim().slice(0, 10);
      }
      const actualEndRaw =
        issue.Actual_End_Date ??
        issue.actual_end_date ??
        issue.Updated_Date ??
        issue.updated_date ??
        issue.Updated_On ??
        issue.updated_on;
      if (actualEndRaw != null && String(actualEndRaw).trim() !== "") {
        payload.Actual_End_Date = String(actualEndRaw).trim().slice(0, 10);
      }
      await updateIssue(issueId, payload);
      let next = null;
      try {
        next = await fetchIssueById(issueId);
      } catch (refetchErr) {
        console.error("Error refetching issue after reassign:", refetchErr);
      }
      if (onUpdated) onUpdated(next ?? issue);
      const reassignedToNames = (selectedUserIds || [])
        .map((id) => users.find((u) => u.id === id)?.name)
        .filter(Boolean);
      setLastReassignedTo(reassignedToNames);
      setLastReassignedBy(getCurrentUserName());
      setShowAssigneesDropdown(false);
      setShowReassignSection(false);
      setReassignError("");
    } catch (err) {
      console.error("Error reassigning:", err);
      setReassignError(err.response?.data?.message || err.response?.data?.error || "Failed to reassign.");
    } finally {
      setSaving(false);
    }
  };

  if (!issue) {
    return (
      <div className="viewlead-card">
        <div className="viewlead-section-title">Issue</div>
        <div className="viewlead-info-value muted">Issue not found.</div>
      </div>
    );
  }

  const testCases = Array.isArray(issue.Test_Cases) ? issue.Test_Cases : [];
  const testCaseIds = Array.isArray(issue.Test_Case_Ids) ? issue.Test_Case_Ids : [];

  // Map Test Case Id -> Title so we can display names instead of just ids
  const testCaseById = new Map();
  for (const tc of testCases) {
    const rawId = tc?.Test_Case_Id ?? tc?.test_case_id ?? tc?.id;
    const numId = Number(rawId);
    if (Number.isFinite(numId)) {
      testCaseById.set(
        numId,
        tc.Title ?? tc.title ?? tc.Test_Case_Title ?? tc.test_case_title ?? `#${numId}`
      );
    }
  }

  return (
    <Fragment>
    <div
      style={{
        display: "flex",
        gap: 16,
        alignItems: "stretch",
        flexWrap: "wrap",
        marginBottom: 16
      }}
    >
      <div className="viewlead-card" style={{ flex: "2 1 0", minWidth: 260 }}>
        <h3 className="viewlead-section-title">Issue Information</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Issue ID" value={issue.Issue_Id != null ? String(issue.Issue_Id) : "—"} />
          <InfoItem label="Title" value={issue.Title || "—"} />
          <InfoItem label="Type" value={issue.Type || "—"} />
          <InfoItem label="Issue Layer" value={issue.Issue_Layer || "—"} />
          <InfoItem label="Priority" value={issue.Priority || "—"} />
          <InfoItem
            label="Project"
            value={
              loadingTaskDetails
                ? "Loading..."
                : taskDetails?.Project_Name ??
                    taskDetails?.project_name ??
                    taskDetails?.Project_Title ??
                    taskDetails?.project_title ??
                    issue?.Project_Name ??
                    issue?.project_name ??
                    (taskDetails?.Project_Id ?? taskDetails?.project_id ?? issue?.Project_Id ?? issue?.project_id ?? "—")
            }
          />
          <InfoItem
            label="Subproject"
            value={
              loadingTaskDetails
                ? "Loading..."
                : taskDetails?.Sub_Project_Name ??
                    taskDetails?.sub_project_name ??
                    taskDetails?.Sub_Project_Title ??
                    taskDetails?.sub_project_title ??
                    issue?.Sub_Project_Name ??
                    issue?.sub_project_name ??
                    (taskDetails?.Sub_Project_Id ?? taskDetails?.sub_project_id ?? issue?.Sub_Project_Id ?? issue?.sub_project_id ?? "—")
            }
          />
          <InfoItem label="Due date" value={formatIssueDueDate(issue.Due_Date ?? issue.due_date)} />
          <InfoItem
            label="Actual end date"
            value={formatIssueDueDate(
              issue.Actual_End_Date ??
                issue.actual_end_date ??
                issue.Updated_Date ??
                issue.updated_date ??
                issue.Updated_On ??
                issue.updated_on
            )}
          />
          <InfoItem label="Status" value={issue.Status || issue.Status_Id || "—"} />
          <InfoItem label="Task" value={issue.Task_Title || issue.Task_Id || "—"} />
          <InfoItem label="Sub task" value={issue.Sub_Task_Title || issue.Sub_Task_Id || "—"} />
        </div>

        {issue.Description ? (
          <>
            <h3 className="viewlead-section-title">Description</h3>
            <div className="viewlead-info-grid">
              <div className="viewlead-info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="viewlead-info-value">{issue.Description}</span>
              </div>
            </div>
          </>
        ) : null}

        {testCaseIds.length > 0 ? (
          <>
            <h3 className="viewlead-section-title">Test Cases</h3>
            <div className="viewlead-info-grid">
              <div className="viewlead-info-item" style={{ gridColumn: "1 / -1" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {testCaseIds.map((id) => (
                    <span
                      key={id}
                      style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: "#E3F2FD",
                        color: "#1565C0",
                        fontSize: 12,
                        fontWeight: 600
                      }}
                    >
                      {testCaseById.get(Number(id)) ?? `#${id}`}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>

      <div style={{ flex: "1 1 0", minWidth: 260, maxWidth: 480, display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="viewlead-card">
          <h3 className="viewlead-section-title">Created</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            <InfoItem label="Created By" value={issue.Created_By_Name || issue.Created_By || "—"} />
            <InfoItem label="Created On" value={formatDateTime(issue.Created_On)} />
          </div>
          <h3 className="viewlead-section-title">Updated</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <InfoItem label="Updated By" value={issue.Updated_By_Name ?? issue.Updated_By ?? "—"} />
            <InfoItem label="Updated On" value={formatDateTime(issue.Updated_On)} />
          </div>
        </div>

        <div className="viewlead-card form-group-multi-select" style={{ position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 8, borderBottom: "1px solid #e8edf2" }}>
            <h3 className="viewlead-section-title" style={{ margin: 0, paddingBottom: 0, borderBottom: "none" }}>Assigned Team Members</h3>
            {canEdit ? (
              <button
                type="button"
                className="btn-subproject-primary"
                onClick={() => {
                  setShowReassignSection((prev) => !prev);
                  if (showReassignSection) setShowAssigneesDropdown(false);
                }}
                style={{
                  background: "transparent",
                  color: "#3182CE",
                  border: "1px solid #3182CE",
                  flexShrink: 0
                }}
              >
                Reassign
              </button>
            ) : null}
          </div>

          {canEdit && showReassignSection ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, marginBottom: 8 }}>
                    <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                      <div
                        className="permissions-select-trigger"
                        onClick={() => {
                          if (!loadingUsers) setShowAssigneesDropdown((prev) => !prev);
                        }}
                        style={{
                          opacity: loadingUsers ? 0.6 : 1,
                          cursor: loadingUsers ? "default" : "pointer"
                        }}
                      >
                        <span className="permissions-select-text">
                          {loadingUsers
                            ? "Loading users..."
                            : selectedUserIds.length === 0
                              ? "Select assignees"
                              : `${selectedUserIds.length} user${selectedUserIds.length > 1 ? "s" : ""} selected`}
                        </span>
                        <FaChevronDown className={`permissions-select-chevron ${showAssigneesDropdown ? "open" : ""}`} />
                      </div>

                      {showAssigneesDropdown && !loadingUsers && (
                        <div
                          className="permissions-select-panel"
                          style={{ maxHeight: 200, overflowY: "auto" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <label className="permissions-select-item">
                            <input type="checkbox" checked={allUsersSelected} onChange={handleToggleSelectAll} />
                            <span className="permissions-select-item-label">Select all</span>
                          </label>
                          {users.map((u) => (
                            <label key={u.id} className="permissions-select-item">
                              <input
                                type="checkbox"
                                checked={selectedUserIds.includes(u.id)}
                                onChange={() => handleUserToggle(u.id)}
                              />
                              <span className="permissions-select-item-label">{u.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn-subproject-primary"
                      onClick={handleReassign}
                      disabled={saving}
                      style={{ flexShrink: 0 }}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>

                  {reassignError ? (
                    <div style={{ color: "#c53030", fontSize: 13, marginTop: 8 }}>{reassignError}</div>
                  ) : null}
                </>
          ) : null}

          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
            {assignedUsers.length > 0 ? (
              assignedUsers.map((u, idx) => {
                const fullName = `${u.First_Name ?? u.first_name ?? ""} ${u.Last_Name ?? u.last_name ?? ""}`.trim();
                const name = u.User_Name ?? u.user_name ?? u.name ?? (fullName || "—");
                return (
                  <div key={u.User_Id ?? u.user_id ?? idx} className="viewlead-info-item">
                    <span className="viewlead-info-value">{name}</span>
                  </div>
                );
              })
            ) : (
              <div className="viewlead-info-item">
                <span className="viewlead-info-value muted">No team members assigned</span>
              </div>
            )}
          </div>

          {lastReassignedBy ? (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #e8edf2" }}>
              <h3 className="viewlead-section-title" style={{ marginBottom: 8 }}>Last Reassignment</h3>
              <InfoItem label="Reassigned by" value={lastReassignedBy || "—"} />
            </div>
          ) : null}
        </div>
      </div>
    </div>

    <div className="viewlead-card issue-attachments-card">
      <IssueFileAttachments
        issue={issue}
        issueId={issue?.Issue_Id ?? issueId}
        canUpload={canEdit}
        onRefresh={refreshIssue}
      />
    </div>
    </Fragment>
  );
}
